import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, Minimize2, Maximize2, ScreenShare, ScreenShareOff, SwitchCamera, SignalHigh, SignalMedium, SignalLow, Lock, Volume1, Volume2, Pause, LayoutGrid, Presentation, X, Users, Settings, MessageSquare, BellRing } from 'lucide-react';
import { Button } from '../ui/button';
import { useGroupCall } from '../../contexts/GroupCallContext';
import { useAuth } from '../../contexts/AuthContext';

const formatDuration = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const initials = (name) => (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const QUALITY_ICONS = { good: SignalHigh, fair: SignalMedium, poor: SignalLow };
const QUALITY_LABELS = { good: 'Good connection', fair: 'Fair connection', poor: 'Poor connection' };

const QualityBadge = ({ quality, dark = true }) => {
  if (!quality) return null;
  const colors = dark
    ? { good: 'text-green-400', fair: 'text-amber-400', poor: 'text-red-400' }
    : { good: 'text-green-600', fair: 'text-amber-500', poor: 'text-red-500' };
  const Icon = QUALITY_ICONS[quality];
  return (
    <span title={QUALITY_LABELS[quality]} aria-label={QUALITY_LABELS[quality]} className="inline-flex" data-testid="group-call-quality-badge" data-quality={quality}>
      <Icon className={`w-3.5 h-3.5 ${colors[quality]}`} />
    </span>
  );
};

const EncryptionBadge = ({ encrypted, dark = true, withText = false }) => {
  if (!encrypted) return null;
  return (
    <span
      title="Encrypted call"
      aria-label="Encrypted call"
      className={`inline-flex items-center gap-1 ${dark ? 'text-green-400' : 'text-green-600'}`}
      data-testid="group-call-encryption-badge"
    >
      <Lock className="w-3 h-3" />
      {withText && <span className="text-[10px] font-medium">Encrypted</span>}
    </span>
  );
};

const TileQualityIcon = ({ quality }) => {
  if (!quality || quality === 'good') return null;
  const Icon = QUALITY_ICONS[quality];
  const color = quality === 'poor' ? 'text-red-400' : 'text-amber-400';
  return <Icon className={`w-3.5 h-3.5 shrink-0 ${color}`} title={QUALITY_LABELS[quality]} />;
};

// Native-shape tiles (matches the 1:1 panel behaviour): every tile keeps its
// video's real aspect ratio, packed into justified rows that fill the screen.
const ASPECT_MIN = 0.5;
const ASPECT_MAX = 2.4;

const clampAspect = (a) => Math.min(ASPECT_MAX, Math.max(ASPECT_MIN, a || 16 / 9));

const computeRowsLayout = (W, H, items, gap) => {
  const n = items.length;
  if (!n || W <= 0 || H <= 0) return [];
  let best = null;
  for (let r = 1; r <= n; r++) {
    const rowH = (H - gap * (r - 1)) / r;
    if (rowH <= 0) break;
    // Contiguous partition balanced by aspect sum (tiles stay in sorted order)
    const totalA = items.reduce((s, it) => s + it.a, 0);
    const target = totalA / r;
    const rows = [];
    let cur = [];
    let curA = 0;
    for (const it of items) {
      if (cur.length && curA + it.a / 2 >= target && rows.length < r - 1) {
        rows.push(cur);
        cur = [];
        curA = 0;
      }
      cur.push(it);
      curA += it.a;
    }
    if (cur.length) rows.push(cur);
    // Row height: shared per row, shrunk only if native widths would overflow
    let minArea = Infinity;
    const laid = rows.map(row => {
      const sumA = row.reduce((s, it) => s + it.a, 0);
      const gaps = gap * (row.length - 1);
      const h = Math.min(rowH, (W - gaps) / sumA);
      row.forEach(it => { minArea = Math.min(minArea, h * h * it.a); });
      return { h, row };
    });
    if (!best || minArea > best.score) best = { score: minArea, laid };
  }
  return best.laid.map(({ h, row }) => ({
    height: Math.max(1, Math.floor(h)),
    items: row.map(it => ({ t: it.t, width: Math.max(1, Math.floor(h * it.a)) })),
  }));
};

const VideoTile = ({ track, name, isLocal, mirrored, isSpeaking, micOn, quality, screen, paused, testId, style, tileKey, onAspect, onClick, fit = 'cover' }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    const el = videoRef.current;
    if (track && el) {
      try { track.attach(el); } catch (e) {}
      return () => { try { track.detach(el); } catch (e) {} };
    }
    return undefined;
  }, [track]);
  const onDims = (e) => {
    const { videoWidth: w, videoHeight: h } = e.target;
    if (w && h && onAspect) onAspect(tileKey, w / h);
  };
  // Screen shares are never cropped; camera tiles fill their cell (U7)
  const objectFit = screen || fit === 'contain' ? 'object-contain' : 'object-cover';
  return (
    <div
      className={`relative rounded-xl overflow-hidden bg-neutral-900 flex items-center justify-center ${onClick ? 'cursor-pointer' : ''}`}
      style={style}
      onClick={onClick}
      title={onClick ? 'Tap to view full screen' : undefined}
      data-testid={testId}
    >
      {track ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          onLoadedMetadata={onDims}
          onResize={onDims}
          className={`w-full h-full ${objectFit}`}
          style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
        />
      ) : (
        <div className="w-16 h-16 rounded-full bg-[#2563eb] text-white flex items-center justify-center text-xl font-semibold">
          {initials(name)}
        </div>
      )}
      {/* Speaking ring drawn ABOVE the video so crop-to-fill can't hide it */}
      {isSpeaking && (
        <div className="absolute inset-0 rounded-xl border-2 border-green-500 pointer-events-none z-10" data-testid={`${testId}-speaking-ring`} />
      )}
      {paused && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px] flex flex-col items-center justify-center gap-1.5 pointer-events-none px-2" data-testid={`${testId}-paused`}>
          <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-400/60 flex items-center justify-center animate-pulse">
            <Pause className="w-5 h-5 text-amber-400" />
          </div>
          <p className="text-xs text-white font-medium text-center">On another call or app…</p>
        </div>
      )}
      <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center gap-1.5">
        <span className="px-2 py-0.5 rounded bg-black/60 text-white text-xs truncate max-w-[75%]">
          {isLocal ? (screen ? 'Your screen' : 'You') : screen ? `${name} (screen)` : name}
        </span>
        {!screen && !micOn && <MicOff className="w-3.5 h-3.5 text-red-400 shrink-0" />}
        {!isLocal && <TileQualityIcon quality={quality} />}
      </div>
    </div>
  );
};

// U6: draining countdown ring around the caller avatar
const CountdownAvatar = ({ name, expiresAt }) => {
  const [frac, setFrac] = useState(1);
  useEffect(() => {
    if (!expiresAt) return undefined;
    const total = Math.max(1000, expiresAt - Date.now());
    const t = setInterval(() => setFrac(Math.max(0, (expiresAt - Date.now()) / total)), 250);
    return () => clearInterval(t);
  }, [expiresAt]);
  const R = 46;
  const C = 2 * Math.PI * R;
  return (
    <div className="relative w-24 h-24 flex items-center justify-center">
      {expiresAt && (
        <svg className="absolute inset-0 w-24 h-24 -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
          <circle
            cx="50" cy="50" r={R} fill="none" stroke="#22c55e" strokeWidth="4" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={C * (1 - frac)}
            style={{ transition: 'stroke-dashoffset 0.3s linear' }}
            data-testid="incoming-call-countdown"
          />
        </svg>
      )}
      <div className="w-20 h-20 rounded-full bg-[#2563eb] text-white flex items-center justify-center text-2xl font-semibold animate-pulse">
        {initials(name)}
      </div>
    </div>
  );
};

// U2: pre-join "green room" — preview + mic/cam choices before connecting
const PrejoinScreen = ({ groupCall, confirmJoin, cancelPrejoin }) => {
  const isVideo = groupCall.media === 'video';
  const [micOn, setMicOn] = useState(() => localStorage.getItem('group_call_prejoin_mic') !== 'off');
  const [camOn, setCamOn] = useState(() => isVideo && localStorage.getItem('group_call_prejoin_cam') !== 'off');
  const [joining, setJoining] = useState(false);
  const [previewAspect, setPreviewAspect] = useState(16 / 9);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const onPreviewDims = (e) => {
    const { videoWidth: w, videoHeight: h } = e.target;
    if (w && h) setPreviewAspect(w / h);
  };

  useEffect(() => {
    let cancelled = false;
    if (isVideo && camOn) {
      navigator.mediaDevices?.getUserMedia({ video: { facingMode: 'user' } })
        .then(stream => {
          if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
          streamRef.current = stream;
          if (videoRef.current) videoRef.current.srcObject = stream;
        })
        .catch(() => setCamOn(false));
    }
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    };
  }, [isVideo, camOn]);

  const join = () => {
    if (joining) return;
    setJoining(true);
    localStorage.setItem('group_call_prejoin_mic', micOn ? 'on' : 'off');
    if (isVideo) localStorage.setItem('group_call_prejoin_cam', camOn ? 'on' : 'off');
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    confirmJoin({ micOn, camOn });
  };

  const toggleCls = (on) => `h-12 w-12 rounded-full ${on ? 'bg-white/20 hover:bg-white/30 text-white' : 'bg-red-600/90 hover:bg-red-600 text-white'}`;

  return (
    <div className="fixed inset-0 z-[9000] bg-neutral-950 flex flex-col items-center justify-center px-6" data-testid="group-call-prejoin">
      <div className="w-full max-w-sm flex flex-col items-center gap-5">
        <div className="text-center">
          <p className="font-semibold text-lg text-white" data-testid="prejoin-conversation-name">{groupCall.conversation_name}</p>
          <p className="text-sm text-neutral-400">
            Ready to join this group {isVideo ? 'video' : 'voice'} call?
            {groupCall.joined_count > 0 && <span> · {groupCall.joined_count} on the call now</span>}
          </p>
        </div>
        <div className="w-full flex items-center justify-center">
          {isVideo && camOn ? (
            // Preview box takes the CAMERA's real shape (like the 1:1 panel) — no cropping
            <div
              className="rounded-2xl overflow-hidden bg-neutral-900 border border-white/10 max-w-full"
              style={previewAspect >= 1
                ? { width: '100%', aspectRatio: previewAspect }
                : { height: 'min(45vh, 420px)', aspectRatio: previewAspect }}
              data-testid="prejoin-preview-box"
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                onLoadedMetadata={onPreviewDims}
                onResize={onPreviewDims}
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
                data-testid="prejoin-camera-preview"
              />
            </div>
          ) : (
            <div className="w-full aspect-video rounded-2xl bg-neutral-900 border border-white/10 flex items-center justify-center" data-testid="prejoin-preview-box">
              <div className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 rounded-full bg-[#2563eb] text-white flex items-center justify-center text-xl font-semibold">
                  {initials(groupCall.conversation_name)}
                </div>
                {isVideo && <p className="text-xs text-neutral-500">Camera is off</p>}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button size="icon" className={toggleCls(micOn)} onClick={() => setMicOn(v => !v)} data-testid="prejoin-mic-toggle" aria-label={micOn ? 'Join with microphone on' : 'Join muted'}>
            {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </Button>
          {isVideo && (
            <Button size="icon" className={toggleCls(camOn)} onClick={() => setCamOn(v => !v)} data-testid="prejoin-cam-toggle" aria-label={camOn ? 'Join with camera on' : 'Join with camera off'}>
              {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
            </Button>
          )}
        </div>
        <div className="w-full flex flex-col items-center gap-2">
          <Button
            className="w-full h-12 rounded-full bg-green-600 hover:bg-green-700 text-white text-base font-semibold"
            onClick={join}
            disabled={joining}
            data-testid="prejoin-join-button"
          >
            <Phone className="w-5 h-5 mr-2" />
            {joining ? 'Joining…' : 'Join now'}
          </Button>
          <Button variant="ghost" className="text-neutral-400 hover:text-white" onClick={cancelPrejoin} disabled={joining} data-testid="prejoin-cancel-button">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

const ROSTER_BADGES = {
  joined: { label: 'In call', cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
  invited: { label: 'Ringing…', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30 animate-pulse' },
  declined: { label: 'Declined', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  missed: { label: 'Missed', cls: 'bg-neutral-500/15 text-neutral-400 border-neutral-500/30' },
  left: { label: 'Left', cls: 'bg-neutral-500/15 text-neutral-400 border-neutral-500/30' },
};
const ROSTER_ORDER = { joined: 0, invited: 1, missed: 2, declined: 3, left: 4 };
const RING_AGAIN_STATES = ['missed', 'declined', 'left'];

const GAP = 12;

export const GroupCallOverlay = () => {
  const {
    groupCall, participants, participantStates, micMuted, cameraOn, callSeconds,
    screenSharing, toggleScreenShare, canScreenShare, cameraFacing, flipCamera, canFlipCamera,
    connectionQuality, encrypted, volumeBoost, toggleVolumeBoost, canBoostVolume,
    dominantSpeaker, soloCountdown, stayOnCall,
    acceptIncoming, declineIncoming, leaveGroupCall, toggleMic, toggleCamera,
    confirmJoin, cancelPrejoin, ringAgain,
    listMediaDevices, switchDevice, activeDevices, canSetSpeaker,
    callChatToast, callChatUnread, clearCallChatUnread,
  } = useGroupCall();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [minimized, setMinimized] = useState(false);
  const [pillPos, setPillPos] = useState(null); // resets to bottom-right on each new call
  const pillDragRef = useRef(null);
  const [aspects, setAspects] = useState({});
  const [gridSize, setGridSize] = useState({ w: 0, h: 0 });
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('group_call_view') || 'speaker');
  const [focusedKey, setFocusedKey] = useState(null);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deviceLists, setDeviceLists] = useState(null);
  const gridRef = useRef(null);
  const isTouchDevice = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

  const toggleViewMode = () => {
    setViewMode(v => {
      const next = v === 'speaker' ? 'grid' : 'speaker';
      localStorage.setItem('group_call_view', next);
      return next;
    });
  };

  useEffect(() => {
    setMinimized(false); setAspects({}); setFocusedKey(null);
    setRosterOpen(false); setSettingsOpen(false); setDeviceLists(null);
    setPillPos(null);
  }, [groupCall?.call_id]);

  // U3: exit full view if the focused participant leaves (or their share stops)
  useEffect(() => {
    if (!focusedKey) return;
    const isScreen = focusedKey.endsWith('-screen');
    const id = focusedKey.replace(/-screen$/, '');
    const p = participants.find(pp => pp.identity === id);
    if (!p || (isScreen && !p.screenTrack)) setFocusedKey(null);
  }, [focusedKey, participants]);

  // U4: load device lists whenever the settings panel opens
  useEffect(() => {
    if (!settingsOpen) return;
    listMediaDevices().then(setDeviceLists);
  }, [settingsOpen, listMediaDevices]);

  const onAspect = useCallback((key, a) => {
    setAspects(m => (Math.abs((m[key] || 0) - a) < 0.01 ? m : { ...m, [key]: a }));
  }, []);

  // Measure the tile area (runs after render; ref is null when the grid isn't shown)
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return undefined;
    const measure = () => setGridSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [minimized, groupCall?.status, viewMode, participants.length]);

  if (!groupCall) return null;

  // ── U2: pre-join green room ──
  if (groupCall.status === 'prejoin') {
    return <PrejoinScreen groupCall={groupCall} confirmJoin={confirmJoin} cancelPrejoin={cancelPrejoin} />;
  }

  // ── Incoming invite — same card style as the 1:1 incoming modal ──
  if (groupCall.status === 'incoming') {
    return (
      <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="incoming-group-call-modal">
        <div className="bg-card border rounded-2xl shadow-2xl p-8 w-[320px] flex flex-col items-center gap-5">
          <CountdownAvatar name={groupCall.initiator_name || groupCall.conversation_name} expiresAt={groupCall.incoming_expires_at} />
          <div className="text-center">
            <p className="font-semibold text-lg" data-testid="group-call-conversation-name">{groupCall.conversation_name}</p>
            <p className="text-sm text-muted-foreground">
              {groupCall.initiator_name || 'Someone'} started a group {groupCall.media === 'video' ? 'video' : 'voice'} call…
            </p>
            {groupCall.joined_count > 0 && (
              <p className="text-xs text-green-600 mt-1" data-testid="group-call-joined-count">
                {groupCall.joined_count} already in the call
              </p>
            )}
          </div>
          <div className="flex items-center gap-6">
            <Button
              onClick={declineIncoming}
              className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-700 text-white"
              aria-label="Decline group call"
              data-testid="group-call-decline-button"
            >
              <PhoneOff className="w-6 h-6" />
            </Button>
            <Button
              onClick={acceptIncoming}
              className="h-14 w-14 rounded-full bg-green-600 hover:bg-green-700 text-white"
              aria-label="Join group call"
              data-testid="group-call-accept-button"
            >
              {groupCall.media === 'video' ? <Video className="w-6 h-6" /> : <Phone className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Minimized pill — draggable, position remembered (U5+) ──
  if (minimized) {
    const onPillPointerDown = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      pillDragRef.current = {
        startX: e.clientX, startY: e.clientY,
        offX: e.clientX - rect.left, offY: e.clientY - rect.top,
        w: rect.width, h: rect.height, moved: false,
      };
      const onMove = (ev) => {
        const d = pillDragRef.current;
        if (!d) return;
        if (!d.moved && Math.abs(ev.clientX - d.startX) < 6 && Math.abs(ev.clientY - d.startY) < 6) return;
        d.moved = true;
        const x = Math.min(Math.max(8, ev.clientX - d.offX), window.innerWidth - d.w - 8);
        const y = Math.min(Math.max(8, ev.clientY - d.offY), window.innerHeight - d.h - 8);
        setPillPos({ x, y });
      };
      const onUp = () => {
        pillDragRef.current = null;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };
    const stopDrag = (e) => e.stopPropagation();
    const pillStyle = pillPos
      ? {
          left: Math.min(Math.max(8, pillPos.x), Math.max(8, window.innerWidth - 60)),
          top: Math.min(Math.max(8, pillPos.y), Math.max(8, window.innerHeight - 60)),
          right: 'auto',
          bottom: 'auto',
          touchAction: 'none',
        }
      : { touchAction: 'none' };
    return (
      <div
        className="fixed bottom-4 right-4 z-[9000] flex items-center gap-3 rounded-full border bg-card shadow-2xl pl-4 pr-2 py-2 cursor-grab active:cursor-grabbing select-none"
        style={pillStyle}
        onPointerDown={onPillPointerDown}
        data-testid="group-call-minimized-bar"
      >
        <span className="w-2.5 h-2.5 rounded-full bg-green-500" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight truncate max-w-[140px]">{groupCall.conversation_name}</p>
          <span className="flex items-center gap-1.5">
            <p className="text-xs text-muted-foreground leading-tight" data-testid="group-call-duration-mini">{formatDuration(callSeconds)}</p>
            <span className="flex items-center gap-0.5 text-xs text-muted-foreground" data-testid="group-call-mini-count">
              <Users className="w-3 h-3" />{participants.length}
            </span>
            {micMuted && <MicOff className="w-3 h-3 text-red-500" data-testid="group-call-mini-muted-icon" />}
            {soloCountdown !== null && <span className="text-xs text-amber-500 font-medium">ends in {soloCountdown}s</span>}
            <QualityBadge quality={connectionQuality} dark={false} />
            <EncryptionBadge encrypted={encrypted && groupCall.status === 'active'} dark={false} />
          </span>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className={`h-9 w-9 rounded-full ${micMuted ? 'text-red-500' : ''}`}
          onClick={toggleMic}
          onPointerDown={stopDrag}
          data-testid="group-call-mini-mute-button"
          aria-label={micMuted ? 'Unmute microphone' : 'Mute microphone'}
        >
          {micMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </Button>
        <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full" onClick={() => setMinimized(false)} onPointerDown={stopDrag} data-testid="group-call-expand-button" aria-label="Expand call">
          <Maximize2 className="w-4 h-4" />
        </Button>
        <Button size="icon" className="h-9 w-9 rounded-full bg-red-600 hover:bg-red-700 text-white" onClick={leaveGroupCall} onPointerDown={stopDrag} data-testid="group-call-leave-mini-button" aria-label="Leave call">
          <PhoneOff className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  // ── Full call screen ──
  // Sort so similar shapes sit together: screen shares → landscape → portrait
  const tiles = [];
  participants.forEach(p => {
    if (p.screenTrack) tiles.push({ key: `${p.identity}-screen`, track: p.screenTrack, screen: true, p });
  });
  participants.forEach(p => tiles.push({ key: p.identity, track: p.cameraTrack, screen: false, p }));
  const rank = (t) => (t.screen ? 0 : (aspects[t.key] || 16 / 9) >= 1 ? 1 : 2);
  tiles.sort((a, b) => rank(a) - rank(b));

  // Native-shape layout: each tile carries its own video aspect (avatar
  // tiles default to 16:9), packed into justified rows filling the area
  const W = gridSize.w || window.innerWidth - 24;
  const H = gridSize.h || window.innerHeight - 160;
  const layoutItems = tiles.map(t => ({ t, a: clampAspect(aspects[t.key]) }));
  const gridRows = computeRowsLayout(W, H, layoutItems, GAP);

  // Speaker spotlight: screen share > dominant speaker > first remote
  const speakerViewActive = viewMode === 'speaker' && tiles.length >= 3;
  let spotlightTile = null;
  if (speakerViewActive) {
    spotlightTile = tiles.find(t => t.screen)
      || (dominantSpeaker && tiles.find(t => !t.screen && t.p.identity === dominantSpeaker))
      || tiles.find(t => !t.screen && !t.p.isLocal)
      || tiles[0];
  }

  const renderTile = (t, style, onClick, fit = 'cover') => (
    <VideoTile
      key={t.key}
      tileKey={t.key}
      onAspect={onAspect}
      onClick={onClick}
      track={t.track}
      name={t.p.name}
      isLocal={t.p.isLocal}
      mirrored={t.p.isLocal && !t.screen && cameraFacing === 'user'}
      isSpeaking={!t.screen && t.p.isSpeaking}
      micOn={t.p.micOn}
      quality={t.p.quality}
      screen={t.screen}
      paused={!t.screen && t.p.away}
      style={style}
      fit={fit}
      testId={t.screen ? `group-call-tile-${t.p.identity}-screen` : `group-call-tile-${t.p.identity}`}
    />
  );
  const btnCls = 'h-12 w-12 rounded-full bg-white/20 hover:bg-white/30 text-white backdrop-blur';
  const iconCls = 'w-5 h-5';
  const isVideoCall = groupCall.media === 'video';

  // U1: roster rows from server participant states (fallback to LiveKit list)
  const rosterRows = Object.entries(participantStates || {})
    .map(([uid, v]) => ({ uid, name: v?.name || 'Unknown', state: v?.state || 'invited' }))
    .sort((a, b) => (ROSTER_ORDER[a.state] ?? 9) - (ROSTER_ORDER[b.state] ?? 9) || a.name.localeCompare(b.name));
  const ringingNames = rosterRows.filter(r => r.state === 'invited' && r.uid !== user?.id).map(r => r.name);

  const openChat = () => {
    clearCallChatUnread();
    setMinimized(true);
    navigate(`/chat?conversation=${groupCall.conversation_id}`);
  };

  return (
    <div className="fixed inset-0 z-[9000] bg-neutral-950 flex flex-col" data-testid="group-call-screen">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="min-w-0">
          <p className="font-semibold truncate">{groupCall.conversation_name}</p>
          <span className="flex items-center gap-1.5 text-xs text-neutral-400">
            {groupCall.status === 'connecting' ? 'Connecting…' : (
              <>
                <span data-testid="group-call-duration">{formatDuration(callSeconds)}</span>
                <span>· {participants.length} on the call</span>
                <QualityBadge quality={connectionQuality} />
                <EncryptionBadge encrypted={encrypted && groupCall.status === 'active'} withText />
              </>
            )}
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          <Button
            size="icon"
            variant="ghost"
            className="relative text-white"
            onClick={openChat}
            data-testid="group-call-chat-button"
            aria-label="Open the conversation"
            title="Open chat"
          >
            <MessageSquare className="w-5 h-5" />
            {callChatUnread > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="group-call-chat-unread">
                {callChatUnread > 9 ? '9+' : callChatUnread}
              </span>
            )}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="text-white"
            onClick={() => { setRosterOpen(v => !v); setSettingsOpen(false); }}
            data-testid="group-call-roster-button"
            aria-label="Show participants"
            title="Participants"
          >
            <Users className="w-5 h-5" />
          </Button>
          {tiles.length >= 3 && (
            <Button
              size="icon"
              variant="ghost"
              className="text-white"
              onClick={toggleViewMode}
              data-testid="group-call-view-toggle"
              aria-label={viewMode === 'speaker' ? 'Switch to grid view' : 'Switch to speaker view'}
              title={viewMode === 'speaker' ? 'Grid view' : 'Speaker view'}
            >
              {viewMode === 'speaker' ? <LayoutGrid className="w-5 h-5" /> : <Presentation className="w-5 h-5" />}
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="text-white"
            onClick={() => { setSettingsOpen(v => !v); setRosterOpen(false); }}
            data-testid="group-call-settings-button"
            aria-label="Call settings"
            title="Devices"
          >
            <Settings className="w-5 h-5" />
          </Button>
          <Button size="icon" variant="ghost" className="text-white" onClick={() => setMinimized(true)} data-testid="group-call-minimize-button" aria-label="Minimize call">
            <Minimize2 className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* U1: compact strip listing who is still being rung */}
      {ringingNames.length > 0 && (
        <div className="mx-4 mb-2 flex items-center gap-2 text-xs text-neutral-300" data-testid="group-call-ringing-strip">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
          <span className="truncate">
            Ringing {ringingNames.slice(0, 3).join(', ')}{ringingNames.length > 3 ? ` +${ringingNames.length - 3}` : ''}…
          </span>
        </div>
      )}

      {soloCountdown !== null && (
        <div className="mx-3 mb-2 flex items-center justify-between gap-3 rounded-lg bg-amber-500/15 border border-amber-400/40 px-4 py-2.5" data-testid="group-call-solo-banner">
          <p className="text-sm text-amber-300">
            You're the only one left — call ends in <span className="font-semibold" data-testid="group-call-solo-countdown">{soloCountdown}s</span>
          </p>
          <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-black rounded-full h-8 px-4" onClick={stayOnCall} data-testid="group-call-stay-button">
            Stay on call
          </Button>
        </div>
      )}

      {/* U10: in-call toast for new chat messages in this conversation */}
      {callChatToast && (
        <button
          onClick={openChat}
          className="absolute top-16 left-1/2 -translate-x-1/2 z-40 max-w-[85%] flex items-center gap-2 rounded-full bg-neutral-800/95 border border-white/10 px-4 py-2 shadow-xl"
          data-testid="group-call-chat-toast"
        >
          <MessageSquare className="w-4 h-4 text-blue-400 shrink-0" />
          <span className="text-xs text-white truncate">
            <span className="font-semibold">{callChatToast.sender}:</span> {callChatToast.text}
          </span>
        </button>
      )}

      {(() => {
        const focusedTile = focusedKey ? tiles.find(t => t.key === focusedKey) : null;
        if (focusedTile) {
          return (
            <div className="flex-1 min-h-0 mx-1 relative" data-testid="group-call-focused-view">
              <div className="w-full h-full cursor-zoom-out" onClick={() => setFocusedKey(null)}>
                {renderTile(focusedTile, { width: '100%', height: '100%' }, undefined, 'contain')}
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="absolute top-2 right-2 rounded-full h-9 w-9 bg-black/50 hover:bg-black/70 text-white"
                onClick={() => setFocusedKey(null)}
                data-testid="group-call-focus-exit"
                aria-label="Exit full view"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          );
        }
        if (speakerViewActive) {
          return (
            <div className="flex-1 min-h-0 mx-3 flex flex-col gap-2 overflow-hidden">
              <div className="flex-1 min-h-0 flex items-center justify-center" data-testid="group-call-spotlight">
                {renderTile(spotlightTile, { width: '100%', height: '100%' }, () => setFocusedKey(spotlightTile.key), 'contain')}
              </div>
              <div className="flex gap-2 justify-center overflow-x-auto flex-none pb-1" data-testid="group-call-filmstrip">
                {tiles.filter(t => t.key !== spotlightTile.key).map(t => (
                  <div key={t.key} className="flex-none">
                    {renderTile(t, { width: Math.round(90 * clampAspect(aspects[t.key])), height: 90 }, () => setFocusedKey(t.key))}
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div ref={gridRef} className="flex-1 min-h-0 mx-3 overflow-hidden">
            <div className="w-full h-full flex flex-col items-center justify-center" style={{ gap: GAP }} data-testid="group-call-grid">
              {gridRows.map((row, i) => (
                <div key={i} className="flex justify-center" style={{ gap: GAP }}>
                  {row.items.map(({ t, width }) => (
                    <React.Fragment key={t.key}>
                      {renderTile(t, { width, height: row.height }, () => setFocusedKey(t.key))}
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* U1: participants roster panel */}
      {rosterOpen && (
        <div className="absolute inset-y-0 right-0 z-30 w-[290px] max-w-[85vw] bg-neutral-900/95 backdrop-blur border-l border-white/10 flex flex-col" data-testid="group-call-roster-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <p className="text-sm font-semibold text-white">Participants ({rosterRows.length})</p>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-white" onClick={() => setRosterOpen(false)} data-testid="group-call-roster-close" aria-label="Close participants">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {rosterRows.length === 0 && (
              <p className="px-4 py-3 text-xs text-neutral-500">Participant details will appear once the call connects.</p>
            )}
            {rosterRows.map(r => {
              const badge = ROSTER_BADGES[r.state] || ROSTER_BADGES.invited;
              const isMe = r.uid === user?.id;
              return (
                <div key={r.uid} className="flex items-center gap-2.5 px-4 py-2" data-testid={`roster-row-${r.uid}`}>
                  <div className="w-8 h-8 rounded-full bg-[#2563eb] text-white flex items-center justify-center text-xs font-semibold shrink-0">
                    {initials(r.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{r.name}{isMe ? ' (You)' : ''}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium shrink-0 ${badge.cls}`} data-state={r.state}>
                    {badge.label}
                  </span>
                  {!isMe && RING_AGAIN_STATES.includes(r.state) && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 shrink-0 text-amber-400 hover:text-amber-300 hover:bg-white/10"
                      onClick={() => ringAgain(r.uid)}
                      data-testid={`roster-ring-again-${r.uid}`}
                      aria-label={`Ring ${r.name} again`}
                      title="Ring again"
                    >
                      <BellRing className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* U4: device settings panel */}
      {settingsOpen && (
        <div className="absolute top-14 right-3 z-30 w-[290px] max-w-[85vw] rounded-xl bg-neutral-900/95 backdrop-blur border border-white/10 p-4 space-y-3 shadow-2xl" data-testid="group-call-settings-panel">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-white">Devices</p>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-white" onClick={() => setSettingsOpen(false)} data-testid="group-call-settings-close" aria-label="Close settings">
              <X className="w-4 h-4" />
            </Button>
          </div>
          {!deviceLists && <p className="text-xs text-neutral-500">Loading devices…</p>}
          {deviceLists && (
            <>
              <div>
                <label className="text-[11px] text-neutral-400 block mb-1">Microphone</label>
                <select
                  className="w-full h-9 rounded-md bg-neutral-800 border border-white/10 text-white text-xs px-2"
                  value={activeDevices.audioinput || ''}
                  onChange={(e) => switchDevice('audioinput', e.target.value)}
                  data-testid="group-call-mic-select"
                >
                  {!activeDevices.audioinput && <option value="">System default</option>}
                  {deviceLists.audioinput.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
                </select>
              </div>
              {isVideoCall && deviceLists.videoinput.length > 0 && (
                <div>
                  <label className="text-[11px] text-neutral-400 block mb-1">Camera</label>
                  <select
                    className="w-full h-9 rounded-md bg-neutral-800 border border-white/10 text-white text-xs px-2"
                    value={activeDevices.videoinput || ''}
                    onChange={(e) => switchDevice('videoinput', e.target.value)}
                    data-testid="group-call-camera-select"
                  >
                    {!activeDevices.videoinput && <option value="">System default</option>}
                    {deviceLists.videoinput.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
                  </select>
                </div>
              )}
              {canSetSpeaker && deviceLists.audiooutput.length > 0 && (
                <div>
                  <label className="text-[11px] text-neutral-400 block mb-1">Speaker</label>
                  <select
                    className="w-full h-9 rounded-md bg-neutral-800 border border-white/10 text-white text-xs px-2"
                    value={activeDevices.audiooutput || ''}
                    onChange={(e) => switchDevice('audiooutput', e.target.value)}
                    data-testid="group-call-speaker-select"
                  >
                    {!activeDevices.audiooutput && <option value="">System default</option>}
                    {deviceLists.audiooutput.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
                  </select>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex items-center justify-center gap-2 py-4">
        <Button
          size="icon"
          className={btnCls}
          onClick={toggleMic}
          data-testid="group-call-mute-button"
          aria-label={micMuted ? 'Unmute microphone' : 'Mute microphone'}
        >
          {micMuted ? <MicOff className={`${iconCls} text-red-400`} /> : <Mic className={iconCls} />}
        </Button>
        {canBoostVolume && (
          <Button
            size="icon"
            className={btnCls}
            onClick={toggleVolumeBoost}
            title={volumeBoost ? 'Loud speaker (tap for normal)' : 'Normal volume (tap for loud)'}
            aria-label={volumeBoost ? 'Switch to normal volume' : 'Switch to loud volume'}
            data-testid="group-call-volume-button"
            data-boost={volumeBoost ? 'on' : 'off'}
          >
            {volumeBoost ? <Volume2 className={`${iconCls} text-green-400`} /> : <Volume1 className={iconCls} />}
          </Button>
        )}
        {isVideoCall && (
          <Button
            size="icon"
            className={btnCls}
            onClick={toggleCamera}
            data-testid="group-call-camera-button"
            aria-label={cameraOn ? 'Turn camera off' : 'Turn camera on'}
          >
            {cameraOn ? <Video className={`${iconCls} text-green-400`} /> : <VideoOff className={iconCls} />}
          </Button>
        )}
        {isVideoCall && cameraOn && !screenSharing && isTouchDevice && canFlipCamera && (
          <Button
            size="icon"
            className={btnCls}
            onClick={flipCamera}
            data-testid="group-call-flip-camera-button"
            aria-label="Switch camera"
          >
            <SwitchCamera className={iconCls} />
          </Button>
        )}
        {isVideoCall && canScreenShare && (
          <Button
            size="icon"
            className={btnCls}
            onClick={toggleScreenShare}
            data-testid="group-call-screenshare-button"
            aria-label={screenSharing ? 'Stop sharing screen' : 'Share screen'}
          >
            {screenSharing ? <ScreenShareOff className={`${iconCls} text-green-400`} /> : <ScreenShare className={iconCls} />}
          </Button>
        )}
        <Button
          size="icon"
          className="rounded-full w-14 h-14 bg-red-600 hover:bg-red-700 text-white"
          onClick={leaveGroupCall}
          data-testid="group-call-leave-button"
          aria-label="Leave call"
        >
          <PhoneOff className="w-6 h-6" />
        </Button>
      </div>
    </div>
  );
};
